/**
 * Navigation Utility
 *
 * Provides utilities for building navigation items from module specs.
 */

import type { TFunction } from 'i18next';
import { moduleRegistry, getNavGroups as registryGetNavGroups } from '../registry';
import type {
  NavGroup,
  NavItem,
  UserContext,
  FeatureFlagContext,
  ModuleSpec,
  NavGroupId
} from '../types';
import { NAV_GROUP_ORDER, NAV_GROUP_I18N_KEYS } from '../types';

/**
 * Get navigation groups filtered by user permissions and feature flags.
 */
export function buildNavGroups(
  userContext: UserContext,
  t: TFunction,
  flagContext?: FeatureFlagContext,
  getNotificationState?: (id: string) => boolean
): NavGroup[] {
  return registryGetNavGroups(userContext, t, flagContext, getNotificationState);
}

/**
 * Check if a module is visible to the current user.
 */
export function isModuleVisible(
  spec: ModuleSpec,
  userContext: UserContext,
  flagContext?: FeatureFlagContext
): boolean {
  // Check feature flag
  if (spec.meta.featureFlag) {
    if (!flagContext?.isEnabled(spec.meta.featureFlag)) {
      return false;
    }
  }

  // Check permissions
  if (spec.permission) {
    if (userContext.isLoading) {
      return true; // Show while loading
    }

    if (!userContext.ability.can(spec.permission.action, spec.permission.subject)) {
      return false;
    }
  }

  return true;
}

/**
 * Get all visible modules for the current user.
 */
export function getVisibleModules(
  userContext: UserContext,
  flagContext?: FeatureFlagContext
): ModuleSpec[] {
  return moduleRegistry.getAll().filter(spec =>
    isModuleVisible(spec, userContext, flagContext)
  );
}

/**
 * Get modules in a specific navigation group.
 */
export function getModulesInGroup(
  groupId: NavGroupId,
  userContext: UserContext,
  flagContext?: FeatureFlagContext
): ModuleSpec[] {
  return moduleRegistry.getAll()
    .filter(spec => spec.nav?.group === groupId)
    .filter(spec => isModuleVisible(spec, userContext, flagContext))
    .sort((a, b) => (a.nav?.order ?? 100) - (b.nav?.order ?? 100));
}

/**
 * Check if a navigation group has any visible items.
 */
export function hasVisibleItemsInGroup(
  groupId: NavGroupId,
  userContext: UserContext,
  flagContext?: FeatureFlagContext
): boolean {
  return getModulesInGroup(groupId, userContext, flagContext).length > 0;
}

/**
 * Get the translated title for a navigation group.
 */
export function getGroupTitle(groupId: NavGroupId, t: TFunction): string {
  const key = NAV_GROUP_I18N_KEYS[groupId];
  return t(key, { defaultValue: groupId });
}

/**
 * Get ordered list of navigation group IDs.
 */
export function getGroupOrder(): NavGroupId[] {
  return [...NAV_GROUP_ORDER];
}

/**
 * Find the active navigation item based on current path.
 */
export function findActiveNavItem(
  pathname: string,
  groups: NavGroup[]
): NavItem | undefined {
  for (const group of groups) {
    for (const item of group.items) {
      if (pathname === item.to) {
        return item;
      }
      // Check children if present
      if (item.children) {
        for (const child of item.children) {
          if (pathname === child.to) {
            return child;
          }
        }
      }
    }
  }

  // Try prefix matching for nested routes
  for (const group of groups) {
    for (const item of group.items) {
      if (pathname.startsWith(item.to) && item.to !== '/') {
        return item;
      }
    }
  }

  return undefined;
}

/**
 * Check if a path is within a module's scope.
 */
export function isPathInModule(pathname: string, moduleId: string): boolean {
  const module = moduleRegistry.getById(moduleId);
  if (!module?.nav?.path) return false;

  if (module.nav.path === '/') {
    return pathname === '/';
  }

  return pathname === module.nav.path || pathname.startsWith(`${module.nav.path}/`);
}
